// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@guestsupply/XDL/types";
import color from "@guestsupply/XDL/Utilities/color";
import { CheckboxStyle } from "@insite/mobius/Checkbox";
import CheckboxPresentationPropsDefault from "@insite/mobius/Checkbox/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

export const CheckboxInputSelect = `span[role="checkbox"]`;

export const CheckboxCheckedSelector = `
    &[aria-checked="true"]:not([aria-disabled="true"]),
    &[aria-checked="mixed"]:not([aria-disabled="true"])
`;

export const CheckboxDefaultSelector = `&[aria-checked="false"]:not([aria-disabled="true"])`;
export const CheckboxDsiabledSelector = `&[aria-disabled="true"]`;
export const CheckboxDsiabledAndCheckedSelector = `&[aria-checked="false"][aria-disabled="true"]`;

const defaultStateStyles = css`
    & ${CheckboxInputSelect} {
        height: 12px;
        min-width: 12px;
        width: 12px;
        border-color: ${color("common.ui4")};
        border-radius: 0;
        background: ${color("common.ui2")};
        &:focus {
            box-shadow: none;
        }
        & + label {
            margin-left: 8px;
        }
    }
`;

const checkedStyle = css`
    & ${CheckboxInputSelect} {
        ${CheckboxCheckedSelector} {
            background: ${color("primary")};
            border-color: ${color("primary")};
            color: ${color("common.ui1")};
        }
    }
`;
const hoverStyle = css`
    &:hover {
        cursor: pointer;
        ${CheckboxInputSelect} {
            ${CheckboxCheckedSelector} {
                background: ${color("common.ui8")};
                border-color: ${color("common.ui8")};
            }
            ${CheckboxDefaultSelector} {
                border-color: ${color("common.ui8")};
            }
        }
    }
`;

const disabledStyle = css`
    ${CheckboxInputSelect} {
        ${CheckboxDsiabledSelector} {
            background: ${color("common.ui8", 0.66)};
            border: 1px solid ${color("common.border")};
        }
        ${CheckboxDsiabledAndCheckedSelector} {
            background: ${color("common.ui2", 0.33)};
        }
    }
`;

export default {
    defaultProps: {
        ...CheckboxPresentationPropsDefault,
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            size: 12,
            weight: 400,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: 13px;
                user-select: none;
            `,
        },
        iconProps: {
            size: 12,
        },
        css: css`
            &&&[disabled] {
                cursor: not-allowed;
            }
            ${defaultStateStyles}
            ${hoverStyle}
            ${checkedStyle}
            ${disabledStyle}
        `,
    },
    groupDefaultProps: {
        labelProps: {
            size: 16,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
        errorProps: {
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            & > ${TypographyStyle as any} {
                &:first-child {
                    margin-bottom: 16px;
                }
                &:empty {
                    display: none;
                }
            }
            ${/* sc-selector */ CheckboxStyle} + ${/* sc-selector */ CheckboxStyle} {
                margin-top: 14px;
            }
        `,
    },
} as xBaseTheme["checkbox"];
